import { useAuth } from '../../context/AuthContext';
import { useEffect } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import MobileBackButton from '../../components/MobileBackButton';

export default function CreditsBillingPage() {
  const { isAuthenticated, loading } = useAuth();
  const router = useRouter();

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      router.push({
        pathname: '/login',
        query: { redirect: router.asPath }
      });
    }
  }, [isAuthenticated, loading, router]);

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="flex-1 p-8 bg-gray-50 dark:bg-gray-900">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center mb-6">
          <MobileBackButton />
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Credits & Billing</h1>
        </div>

        {/* Introduction */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-6 mb-8 rounded-r-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-500 dark:text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">Understanding CreoleCentric Credits</h3>
              <p className="mt-2 text-sm text-blue-700 dark:text-blue-300">
                Learn how credits work, manage your subscription, optimize usage for cost-effective TTS generation,
                and choose the right payment method for your needs.
              </p>
            </div>
          </div>
        </div>

        {/* Credit Calculation */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 mb-8">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">How Credits Are Calculated</h2>
          </div>
          <div className="p-6">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-700 dark:to-blue-800 rounded-lg p-6 text-white mb-6">
              <h3 className="text-2xl font-bold mb-2">1 Credit = 1 Character</h3>
              <p className="text-blue-100">
                It's that simple! Every character in your text costs exactly 1 credit, including letters, numbers,
                spaces, and punctuation marks.
              </p>
            </div>

            <div className="space-y-4">
              <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 p-4 rounded-r-lg">
                <h4 className="font-medium text-green-900 dark:text-green-100 mb-2">✅ What's Included</h4>
                <ul className="text-sm text-green-800 dark:text-green-200 space-y-1">
                  <li>• All letters (A-Z, a-z, Haitian Creole characters)</li>
                  <li>• Numbers (0-9)</li>
                  <li>• Spaces between words</li>
                  <li>• Punctuation marks (. , ! ? : ; etc.)</li>
                  <li>• Special characters (" ' - / etc.)</li>
                </ul>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4 rounded-r-lg">
                <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">📊 Example Calculations</h4>
                <div className="space-y-3">
                  <div className="bg-white dark:bg-gray-800 p-3 rounded-md">
                    <p className="text-sm font-mono text-gray-700 dark:text-gray-300 mb-1">
                      "Bonjou!"
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      7 characters = <strong className="text-blue-600 dark:text-blue-400">7 credits</strong>
                    </p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 p-3 rounded-md">
                    <p className="text-sm font-mono text-gray-700 dark:text-gray-300 mb-1">
                      "Kijan ou ye jodi a?"
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      19 characters = <strong className="text-blue-600 dark:text-blue-400">19 credits</strong>
                    </p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 p-3 rounded-md">
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                      1,000 word document (~5,000 characters)
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      Approximately <strong className="text-blue-600 dark:text-blue-400">5,000 credits</strong>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Subscription Plans vs Credits */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 mb-8">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Subscription Plans vs. Purchased Credits</h2>
          </div>
          <div className="p-6">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {/* Subscription Credits */}
              <div className="border-2 border-blue-500 rounded-lg p-6 bg-blue-50 dark:bg-blue-900/20">
                <h3 className="text-xl font-semibold text-blue-900 dark:text-blue-100 mb-4 flex items-center">
                  <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                  </svg>
                  Subscription Credits
                </h3>
                <div className="space-y-3 text-sm">
                  <div>
                    <p className="font-medium text-blue-900 dark:text-blue-100 mb-1">📅 Monthly Allocation</p>
                    <p className="text-blue-800 dark:text-blue-200">
                      Credits are automatically added to your account each billing cycle
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-blue-900 dark:text-blue-100 mb-1">🔄 Rollover (Creator+)</p>
                    <p className="text-blue-800 dark:text-blue-200">
                      Unused credits roll over up to 3x your monthly allocation
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-blue-900 dark:text-blue-100 mb-1">⏳ Expiration</p>
                    <p className="text-blue-800 dark:text-blue-200">
                      Credits expire if not used (Free/Learner) or when you reach rollover limit
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-blue-900 dark:text-blue-100 mb-1">💰 Cost-Effective</p>
                    <p className="text-blue-800 dark:text-blue-200">
                      Best value for regular, predictable usage
                    </p>
                  </div>
                </div>
              </div>

              {/* Purchased Credits */}
              <div className="border-2 border-green-500 rounded-lg p-6 bg-green-50 dark:bg-green-900/20">
                <h3 className="text-xl font-semibold text-green-900 dark:text-green-100 mb-4 flex items-center">
                  <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                  </svg>
                  Purchased Credits
                </h3>
                <div className="space-y-3 text-sm">
                  <div>
                    <p className="font-medium text-green-900 dark:text-green-100 mb-1">💎 One-Time Purchase</p>
                    <p className="text-green-800 dark:text-green-200">
                      Buy credits packages as needed with no recurring charges
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-green-900 dark:text-green-100 mb-1">♾️ Never Expire</p>
                    <p className="text-green-800 dark:text-green-200">
                      Purchased credits remain in your account forever - no expiration
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-green-900 dark:text-green-100 mb-1">🎯 Backup Credits</p>
                    <p className="text-green-800 dark:text-green-200">
                      Used only after subscription credits are depleted
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-green-900 dark:text-green-100 mb-1">📈 Flexible</p>
                    <p className="text-green-800 dark:text-green-200">
                      Perfect for irregular usage or extra capacity
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-purple-50 dark:bg-purple-900/20 border-l-4 border-purple-500 p-4 rounded-r-lg">
              <h4 className="font-medium text-purple-900 dark:text-purple-100 mb-2">💡 Pro Tip: Credit Usage Priority</h4>
              <p className="text-sm text-purple-800 dark:text-purple-200">
                When you have both subscription and purchased credits, CreoleCentric automatically uses subscription
                credits first. This preserves your purchased credits as a permanent backup, ensuring you never run out.
              </p>
            </div>
          </div>
        </div>

        {/* Plan Comparison Table */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 mb-8">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Plan Comparison</h2>
          </div>
          <div className="p-6">
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Compare features across all subscription tiers to find the right plan for your needs.
            </p>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 px-2 font-semibold text-gray-900 dark:text-white">Feature</th>
                    <th className="text-center py-3 px-2 font-semibold text-gray-900 dark:text-white">Free</th>
                    <th className="text-center py-3 px-2 font-semibold text-gray-900 dark:text-white">Learner<br/><span className="text-xs font-normal text-gray-500">$5/mo</span></th>
                    <th className="text-center py-3 px-2 font-semibold text-gray-900 dark:text-white">Creator<br/><span className="text-xs font-normal text-gray-500">$15/mo</span></th>
                    <th className="text-center py-3 px-2 font-semibold text-gray-900 dark:text-white">Pro<br/><span className="text-xs font-normal text-gray-500">$65/mo</span></th>
                    <th className="text-center py-3 px-2 font-semibold text-gray-900 dark:text-white">Business<br/><span className="text-xs font-normal text-gray-500">$205/mo</span></th>
                    <th className="text-center py-3 px-2 font-semibold text-gray-900 dark:text-white">Enterprise<br/><span className="text-xs font-normal text-gray-500">$825/mo</span></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  <tr>
                    <td className="py-3 px-2 text-gray-700 dark:text-gray-300">Monthly Credits</td>
                    <td className="py-3 px-2 text-center text-gray-700 dark:text-gray-300">3,000</td>
                    <td className="py-3 px-2 text-center text-gray-700 dark:text-gray-300">30,000</td>
                    <td className="py-3 px-2 text-center text-gray-700 dark:text-gray-300">100,000</td>
                    <td className="py-3 px-2 text-center text-gray-700 dark:text-gray-300">500,000</td>
                    <td className="py-3 px-2 text-center text-gray-700 dark:text-gray-300">2,000,000</td>
                    <td className="py-3 px-2 text-center text-gray-700 dark:text-gray-300">11,000,000</td>
                  </tr>
                  <tr className="bg-gray-50 dark:bg-gray-900/30">
                    <td className="py-3 px-2 text-gray-700 dark:text-gray-300">Chars/Request</td>
                    <td className="py-3 px-2 text-center text-gray-700 dark:text-gray-300">1,000</td>
                    <td className="py-3 px-2 text-center text-gray-700 dark:text-gray-300">3,000</td>
                    <td className="py-3 px-2 text-center text-gray-700 dark:text-gray-300">5,000</td>
                    <td className="py-3 px-2 text-center text-gray-700 dark:text-gray-300">7,500</td>
                    <td className="py-3 px-2 text-center text-gray-700 dark:text-gray-300">7,500</td>
                    <td className="py-3 px-2 text-center text-gray-700 dark:text-gray-300">10,000</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-2 text-gray-700 dark:text-gray-300">API Access</td>
                    <td className="py-3 px-2 text-center text-red-500">&#10007;</td>
                    <td className="py-3 px-2 text-center text-red-500">&#10007;</td>
                    <td className="py-3 px-2 text-center text-green-500">&#10003;</td>
                    <td className="py-3 px-2 text-center text-green-500">&#10003;</td>
                    <td className="py-3 px-2 text-center text-green-500">&#10003;</td>
                    <td className="py-3 px-2 text-center text-green-500">&#10003;</td>
                  </tr>
                  <tr className="bg-gray-50 dark:bg-gray-900/30">
                    <td className="py-3 px-2 text-gray-700 dark:text-gray-300">Priority Queue</td>
                    <td className="py-3 px-2 text-center text-red-500">&#10007;</td>
                    <td className="py-3 px-2 text-center text-red-500">&#10007;</td>
                    <td className="py-3 px-2 text-center text-green-500">&#10003;</td>
                    <td className="py-3 px-2 text-center text-green-500">&#10003;</td>
                    <td className="py-3 px-2 text-center text-green-500">&#10003;</td>
                    <td className="py-3 px-2 text-center text-green-500">&#10003;</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-2 text-gray-700 dark:text-gray-300">Credit Rollover</td>
                    <td className="py-3 px-2 text-center text-red-500">&#10007;</td>
                    <td className="py-3 px-2 text-center text-red-500">&#10007;</td>
                    <td className="py-3 px-2 text-center text-green-500">&#10003;</td>
                    <td className="py-3 px-2 text-center text-green-500">&#10003;</td>
                    <td className="py-3 px-2 text-center text-green-500">&#10003;</td>
                    <td className="py-3 px-2 text-center text-green-500">&#10003;</td>
                  </tr>
                  <tr className="bg-gray-50 dark:bg-gray-900/30">
                    <td className="py-3 px-2 text-gray-700 dark:text-gray-300">Team Seats</td>
                    <td className="py-3 px-2 text-center text-gray-700 dark:text-gray-300">1</td>
                    <td className="py-3 px-2 text-center text-gray-700 dark:text-gray-300">1</td>
                    <td className="py-3 px-2 text-center text-gray-700 dark:text-gray-300">1</td>
                    <td className="py-3 px-2 text-center text-gray-700 dark:text-gray-300">1</td>
                    <td className="py-3 px-2 text-center text-gray-700 dark:text-gray-300">3</td>
                    <td className="py-3 px-2 text-center text-gray-700 dark:text-gray-300">5</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-2 text-gray-700 dark:text-gray-300">Support</td>
                    <td className="py-3 px-2 text-center text-gray-700 dark:text-gray-300">Standard</td>
                    <td className="py-3 px-2 text-center text-gray-700 dark:text-gray-300">Standard</td>
                    <td className="py-3 px-2 text-center text-gray-700 dark:text-gray-300">Standard</td>
                    <td className="py-3 px-2 text-center text-gray-700 dark:text-gray-300">Priority</td>
                    <td className="py-3 px-2 text-center text-gray-700 dark:text-gray-300">Dedicated</td>
                    <td className="py-3 px-2 text-center text-gray-700 dark:text-gray-300">24/7 SLA</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-6 bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 p-4 rounded-r-lg">
              <h4 className="font-medium text-yellow-900 dark:text-yellow-100 mb-2">Streaming Mode Credit Multiplier</h4>
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                Streaming mode (available on Learner+ plans, supported by Creole Regional and Creole Studio Pro)
                charges 1.5x credits for near-instant playback. For example, a 1,000-character text costs
                1,500 credits in Streaming mode vs. 1,000 credits in Standard mode.
              </p>
            </div>
          </div>
        </div>

        {/* Credit Rollover */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 mb-8">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Credit Rollover Policies</h2>
          </div>
          <div className="p-6">
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Credit rollover helps you avoid losing unused credits. Here's how it works across different plans:
            </p>

            <div className="space-y-4">
              <div className="border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 rounded-lg p-5">
                <h3 className="font-semibold text-red-900 dark:text-red-100 mb-2">❌ Free & Learner Plans</h3>
                <p className="text-sm text-red-800 dark:text-red-200">
                  No rollover. Credits expire at the end of each billing period. Use them or lose them!
                </p>
              </div>

              <div className="border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 rounded-lg p-5">
                <h3 className="font-semibold text-green-900 dark:text-green-100 mb-3">✅ Creator, Pro, Business & Enterprise Plans</h3>
                <p className="text-sm text-green-800 dark:text-green-200 mb-4">
                  Automatic rollover with a maximum balance of <strong>3x your monthly allocation</strong>.
                </p>
                <div className="bg-white dark:bg-gray-800 p-4 rounded-md">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-3">Example: Creator Plan</h4>
                  <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                    <p>• Monthly allocation: <strong>100,000 credits</strong></p>
                    <p>• Maximum balance: <strong>300,000 credits</strong> (3x monthly)</p>
                    <p className="pt-2 border-t border-gray-300 dark:border-gray-600 mt-2">
                      <strong>Scenario:</strong> You use only 60,000 credits in Month 1
                    </p>
                    <ul className="ml-4 space-y-1">
                      <li>→ Remaining: 40,000 credits roll over</li>
                      <li>→ Month 2 starts: 40,000 + 100,000 = 140,000 total credits</li>
                      <li>→ You can accumulate up to 300,000 before hitting the cap</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 p-4 rounded-r-lg">
              <h4 className="font-medium text-yellow-900 dark:text-yellow-100 mb-2">⚠️ Important: Downgrade Warning</h4>
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                If you downgrade from a plan with rollover to one without, you'll lose credits exceeding
                the new plan's limits. Use excess credits before your downgrade takes effect!
              </p>
            </div>
          </div>
        </div>

        {/* Creole Centric for Community — nonprofit + community-org discount */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-amber-200 dark:border-amber-800 mb-8">
          <div className="px-6 py-4 border-b border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 rounded-t-lg">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <span>🤝</span> Creole Centric for Community
            </h2>
          </div>
          <div className="p-6 space-y-4 text-sm text-gray-700 dark:text-gray-300">
            <p>
              Free or heavily-subsidized access for the nonprofits and community
              organizations serving the Kreyòl-speaking community — in Haiti, the
              diaspora, and everywhere in between. Admin-approved via a public
              application; not self-serve at checkout.
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="border border-amber-200 dark:border-amber-800/50 rounded p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">🇭🇹 Community Grant (Haiti)</h3>
                <ul className="space-y-1 mt-2">
                  <li>• <strong>Community</strong> — 30K credits/mo · $0</li>
                  <li>• <strong>Community Plus</strong> — 100K credits/mo · $5/mo</li>
                  <li>• <strong>Community Pro</strong> — 500K credits/mo · $22/mo</li>
                </ul>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Baseline tier default on approval. Plus/Pro upgrade via self-serve
                  checkout (Stripe / PayPal / crypto) or admin escalation.
                </p>
              </div>

              <div className="border border-amber-200 dark:border-amber-800/50 rounded p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">🌎 Mission Partner (diaspora)</h3>
                <ul className="space-y-1 mt-2">
                  <li>• <strong>50% off</strong> any paid subscription tier</li>
                  <li>• Applied automatically at checkout — no code needed</li>
                  <li>• Recurs on renewals through the grant term</li>
                </ul>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  For diaspora and international nonprofits + community organizations
                  serving the Kreyòl-speaking community.
                </p>
              </div>
            </div>

            <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 p-3 rounded-r text-xs">
              <strong>Attribution required.</strong> Grantees at all tiers display
              a "Powered by CreoleCentric" badge on their website. Copy-paste HTML
              snippet + SVG asset available at{' '}
              <a href="/attribution" className="underline text-amber-700 dark:text-amber-300">
                /attribution
              </a>.
            </div>

            <div className="flex flex-wrap gap-2 pt-2">
              <a href="/for-community"
                 className="inline-block bg-amber-600 hover:bg-amber-700 text-white font-semibold px-4 py-2 rounded text-sm">
                Learn more &amp; apply
              </a>
              <a href="/attribution"
                 className="inline-block border border-amber-500 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/30 font-semibold px-4 py-2 rounded text-sm">
                Attribution requirement
              </a>
            </div>
          </div>
        </div>

        {/* Payment Methods */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 mb-8">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Payment Methods</h2>
          </div>
          <div className="p-6">
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              CreoleCentric accepts multiple payment methods to serve our global community:
            </p>

            <div className="space-y-6">
              {/* Credit/Debit Cards */}
              <div className="border border-blue-200 dark:border-blue-800 rounded-lg p-5 bg-blue-50 dark:bg-blue-900/20">
                <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-3 flex items-center">
                  <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                    <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
                  </svg>
                  Credit & Debit Cards (Stripe)
                </h3>
                <div className="space-y-2 text-sm text-blue-800 dark:text-blue-200">
                  <p><strong>Accepted Cards:</strong> Visa, Mastercard, American Express, Discover</p>
                  <p><strong>Auto-Renewal:</strong> Yes - perfect for subscriptions</p>
                  <p><strong>Processing Time:</strong> Instant</p>
                  <p><strong>Security:</strong> PCI-DSS compliant via Stripe</p>
                  <p><strong>Best For:</strong> Subscriptions and quick purchases</p>
                </div>
              </div>

              {/* PayPal */}
              <div className="border border-purple-200 dark:border-purple-800 rounded-lg p-5 bg-purple-50 dark:bg-purple-900/20">
                <h3 className="text-lg font-semibold text-purple-900 dark:text-purple-100 mb-3 flex items-center">
                  <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 2a4 4 0 00-4 4v1H5a1 1 0 00-.994.89l-1 9A1 1 0 004 18h12a1 1 0 00.994-1.11l-1-9A1 1 0 0015 7h-1V6a4 4 0 00-4-4zm2 5V6a2 2 0 10-4 0v1h4zm-6 3a1 1 0 112 0 1 1 0 01-2 0zm7-1a1 1 0 100 2 1 1 0 000-2z" clipRule="evenodd" />
                  </svg>
                  PayPal
                </h3>
                <div className="space-y-2 text-sm text-purple-800 dark:text-purple-200">
                  <p><strong>Account Types:</strong> Personal and Business PayPal accounts</p>
                  <p><strong>Auto-Renewal:</strong> Yes - supports automatic subscriptions</p>
                  <p><strong>Processing Time:</strong> Instant</p>
                  <p><strong>Security:</strong> PayPal Buyer Protection</p>
                  <p><strong>Best For:</strong> International users and those with PayPal balance</p>
                </div>
              </div>

              {/* Cryptocurrency */}
              <div className="border border-orange-200 dark:border-orange-800 rounded-lg p-5 bg-orange-50 dark:bg-orange-900/20">
                <h3 className="text-lg font-semibold text-orange-900 dark:text-orange-100 mb-3 flex items-center">
                  <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                  </svg>
                  Cryptocurrency (NOWPayments)
                </h3>
                <div className="space-y-2 text-sm text-orange-800 dark:text-orange-200 mb-3">
                  <p><strong>Supported Coins:</strong> Bitcoin (BTC), Ethereum (ETH), USDT, USDC, and 150+ others</p>
                  <p><strong>Auto-Renewal:</strong> No - manual renewal required each billing period</p>
                  <p><strong>Processing Time:</strong> 10-60 minutes (blockchain confirmation)</p>
                  <p><strong>Security:</strong> Blockchain-verified transactions</p>
                  <p><strong>Best For:</strong> Privacy-focused users, international payments</p>
                </div>
                <div className="bg-white dark:bg-gray-800 p-3 rounded-md">
                  <p className="text-xs text-gray-700 dark:text-gray-300">
                    <strong>💡 Tip:</strong> Use stablecoins (USDT/USDC) to avoid price volatility.
                    You'll receive email reminders before subscription expires.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Managing Subscription */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 mb-8">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Managing Your Subscription</h2>
          </div>
          <div className="p-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-5">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">⬆️ Upgrading</h3>
                <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                  <li>• Takes effect immediately</li>
                  <li>• Prorated credit for remaining time on old plan</li>
                  <li>• New credits added instantly</li>
                  <li>• Increased limits apply right away</li>
                </ul>
              </div>

              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-5">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">⬇️ Downgrading</h3>
                <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                  <li>• Scheduled for next billing date</li>
                  <li>• Keep current benefits until then</li>
                  <li>• May lose excess rolled-over credits</li>
                  <li>• Warning shown before confirming</li>
                </ul>
              </div>

              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-5">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">🔄 Switching Billing Cycles</h3>
                <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                  <li>• Monthly ↔ Yearly switching available</li>
                  <li>• Save up to 17% with annual billing</li>
                  <li>• Changes apply at next renewal</li>
                  <li>• Full year credits given upfront (annual)</li>
                </ul>
              </div>

              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-5">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">❌ Cancellation</h3>
                <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                  <li>• Cancel anytime, no penalties</li>
                  <li>• Access continues until period ends</li>
                  <li>• Unused credits preserved if purchased</li>
                  <li>• Reactivate anytime</li>
                </ul>
              </div>
            </div>

            <div className="mt-6">
              <Link href="/subscription" className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors">
                Manage Subscription
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          </div>
        </div>

        {/* Billing History & Receipts */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 mb-8">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Billing History & Receipts</h2>
          </div>
          <div className="p-6 space-y-6">
            <p className="text-gray-600 dark:text-gray-300">
              You can view every subscription payment on your account and print a receipt for
              each one — useful for expense reports, reimbursement claims, or your own records.
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-5">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">🧾 Billing history</h3>
                <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                  <li>• Lists every subscription payment on your account</li>
                  <li>• Shows plan, amount, payment method, and the paid-through date</li>
                  <li>• Open it from <strong>My Account → Credits → Billing history</strong>, or from the Credits page under Actions</li>
                </ul>
                <div className="mt-4">
                  <Link href="/billing-history" className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline">
                    Open Billing history →
                  </Link>
                </div>
              </div>

              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-5">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">📄 Printable receipts</h3>
                <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                  <li>• Every payment row has a <strong>View receipt</strong> link</li>
                  <li>• Includes plan, billing period, amount, and a receipt number for support</li>
                  <li>• <strong>Print / Save as PDF</strong> button prints only the receipt (no site chrome)</li>
                  <li>• Saved PDF filename is <code className="text-xs">CCL_Receipt_MMDDYY-MMDDYY</code> so a folder of receipts sorts chronologically</li>
                </ul>
              </div>
            </div>

            <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4 text-sm text-blue-900 dark:text-blue-100">
              <p className="font-semibold mb-1">📱 Paid through Apple (in-app purchase)?</p>
              <p>
                Apple owns the receipt for in-app purchases. On Apple rows, the action link says
                <strong> View in App Store</strong> and opens{' '}
                <a href="https://apps.apple.com/account/billing" target="_blank" rel="noopener noreferrer" className="underline">
                  apps.apple.com/account/billing
                </a>{' '}
                in a new tab.
              </p>
            </div>

            <div className="rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 p-4 text-sm text-gray-700 dark:text-gray-300">
              <p className="font-semibold text-gray-900 dark:text-white mb-1">Failed payments and refunds</p>
              <p>
                <strong>Failed payments do not appear in Billing history.</strong> A declined
                charge doesn't create a payment record on your account, so there's nothing to
                receipt. If you were expecting a receipt for a charge that didn't appear here,
                please contact support.
              </p>
              <p className="mt-2">
                Refunds are shown as separate entries in your Credits transaction history (not
                on this page), and never go below zero.
              </p>
            </div>
          </div>
        </div>

        {/* Optimizing Usage */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 mb-8">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Optimizing Credit Usage</h2>
          </div>
          <div className="p-6">
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Get the most value from your credits with these optimization tips:
            </p>

            <div className="space-y-4">
              <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 p-4 rounded-r-lg">
                <h4 className="font-medium text-green-900 dark:text-green-100 mb-2">✂️ Remove Unnecessary Content</h4>
                <p className="text-sm text-green-800 dark:text-green-200">
                  Clean up your text before generating audio. Remove extra spaces, unnecessary formatting characters,
                  and duplicate content. Every character counts!
                </p>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4 rounded-r-lg">
                <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">📝 Use Abbreviations Wisely</h4>
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  "Dr." uses 3 characters vs "Doktè" uses 5 characters in output. However, ensure clarity
                  and pronunciation quality aren't sacrificed for minimal credit savings.
                </p>
              </div>

              <div className="bg-purple-50 dark:bg-purple-900/20 border-l-4 border-purple-500 p-4 rounded-r-lg">
                <h4 className="font-medium text-purple-900 dark:text-purple-100 mb-2">🎯 Test Before Batch Processing</h4>
                <p className="text-sm text-purple-800 dark:text-purple-200">
                  Generate a small sample first to verify voice selection and settings are correct.
                  This prevents wasting credits on large batches that need regeneration.
                </p>
              </div>

              <div className="bg-orange-50 dark:bg-orange-900/20 border-l-4 border-orange-500 p-4 rounded-r-lg">
                <h4 className="font-medium text-orange-900 dark:text-orange-100 mb-2">💾 Reuse Generated Audio</h4>
                <p className="text-sm text-orange-800 dark:text-orange-200">
                  Downloaded audio files are yours forever. Keep organized backups to avoid regenerating
                  the same content. Access your history for 30 days of free re-downloads.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-700 dark:to-blue-800 rounded-lg p-8 text-white">
          <h2 className="text-2xl font-bold mb-4">Ready to Choose Your Plan?</h2>
          <p className="mb-6 text-blue-100">
            Compare all subscription plans and credit packages. Find the perfect option for your needs and budget.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <Link href="/subscription?tab=subscription" className="bg-white text-blue-600 px-6 py-3 rounded-lg font-medium hover:bg-blue-50 transition-colors text-center">
              View Subscription Plans
            </Link>
            <Link href="/subscription?tab=credits" className="bg-blue-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-400 transition-colors text-center">
              Buy Credit Packages
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
